import { useTranslationContext } from 'contexts/TranslationContext/TranslationContext';
import { useZendesk } from 'features/zendesk/hooks/useZendesk';
import { saveHelpRequestTimestamp } from 'features/signUp/utils/helpRequestUtils';
import { useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useCobrand } from 'features/cobrand/hooks/useCobrandUtils';
import { CobrandIdentifier } from 'features/cobrand/types';
import { useSignupData } from './useSignupData';
import { SignupFormData } from '../types';
import { SIGNUP_SOURCE_SCREENS } from '../constants';

type HelpDialogState = 'form' | 'consent' | 'confirmation';

interface UseSignupHelpDialogProps {
  signUpFormData: SignupFormData | null;
  onHelpDialogClose: () => void;
}

export const useSignupHelpDialog = ({
  signUpFormData,
  onHelpDialogClose,
}: UseSignupHelpDialogProps) => {
  const { hospitalID, currentStep, userType } = useSignupData();
  const signUpDataForHelpDialog: HelpDialogData = useMemo(() => {
    const firstName =
      (userType === 'member'
        ? signUpFormData?.memberFirstName
        : signUpFormData?.caregiverFirstName) || '';
    const lastName =
      (userType === 'member'
        ? signUpFormData?.memberLastName
        : signUpFormData?.caregiverLastName) || '';

    return {
      name: `${firstName} ${lastName}`.trim(),
      memberID: signUpFormData?.memberId || '',
      email: signUpFormData?.memberEmail || '',
      dateOfBirth: signUpFormData?.memberDateOfBirth || '',
      phoneNumber:
        (userType === 'member'
          ? signUpFormData?.memberPhoneNumber
          : signUpFormData?.caregiverPhoneNumber) || '',
    };
  }, [userType, signUpFormData]);

  const {
    control,
    getValues,
    reset: resetForm,
    formState: { isValid },
  } = useForm<HelpDialogData>({
    mode: 'onTouched',
    values: signUpDataForHelpDialog,
  });
  const { cobrand } = useCobrand();
  const { translation } = useTranslationContext();
  const { fileOboardingHelpTicket } = useZendesk();
  const [currentState, setCurrentState] = useState<HelpDialogState>('form');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasError, setHasError] = useState(false);
  const formData = getValues();

  const invitedByoptions = useMemo(
    () => [
      {
        label: translation('invited_by_option_insurance_company'),
        value: 'My Insurance Company',
      },
      { label: translation('invited_by_option_hospital'), value: 'My Hospital or Clinic' },
      {
        label: translation('invited_by_option_other'),
        value: 'Other',
      },
    ],
    [translation],
  );

  const transitions = useMemo(
    () => ({
      form: () => setCurrentState('consent'),
      consent: () => setCurrentState('confirmation'),
      reset: () => {
        resetForm();
        setCurrentState('form');
        setHasError(false);
      },
    }),
    [resetForm],
  );

  const handleClose = useCallback(() => {
    transitions.reset(); // Reset to the initial state
    onHelpDialogClose();
  }, [transitions, onHelpDialogClose]);

  const handleFormSubmitClick = useCallback(() => {
    transitions.form();
  }, [transitions]);

  const handleBackButtonClick = useCallback(() => setCurrentState('form'), []);

  const getModalTitle = useMemo(() => {
    if (currentState === 'form') {
      return translation('i_need_help');
    }
    if (currentState === 'consent') {
      return translation('consent_agreement_title');
    }
    return translation('successfully_submitted');
  }, [currentState]);

  const clearError = useCallback(() => {
    setHasError(false);
  }, []);

  const handleAgree = useCallback(async () => {
    try {
      setIsSubmitting(true);
      setHasError(false);
      await fileOboardingHelpTicket({
        ...formData, // Overwrite with form data if there are any changes
        // Extra fields needed for the help ticket apart from fields shown in the Help Form
        hospitalID,
        sourceScreen: SIGNUP_SOURCE_SCREENS[currentStep],
        accessCode: signUpFormData?.accessCode || '',
        stateOfResidence: signUpFormData?.memberStateOfResidence || '',
      });

      saveHelpRequestTimestamp();
      transitions.consent(); // Move to confirmation state
    } catch (err) {
      setHasError(true);
      console.error('Error submitting help request:', err);
    } finally {
      setIsSubmitting(false);
    }
  }, [
    formData,
    hospitalID,
    currentStep,
    signUpFormData?.accessCode,
    signUpFormData?.memberStateOfResidence,
    fileOboardingHelpTicket,
    transitions,
  ]);

  const handleButtonClick = useCallback(() => {
    switch (currentState) {
      case 'form':
        handleFormSubmitClick();
        break;
      case 'consent':
        handleAgree();
        break;
      case 'confirmation':
        handleClose();
        break;
      default:
        break;
    }
  }, [currentState, handleFormSubmitClick, handleAgree, handleClose]);

  const submitButtonText = useMemo(() => {
    if (currentState === 'form') {
      return translation('continue_button_title');
    }
    if (currentState === 'consent') {
      return translation('agree_button');
    }
    return translation('general_ok');
  }, [currentState]);

  return {
    currentState,
    transitions,
    handleClose,
    handleBackButtonClick,
    handleButtonClick,
    modalTitle: getModalTitle,
    showBackButton: currentState === 'consent',
    formControl: control,
    invitedByoptions,
    isSubmitting,
    showInvitedBy:
      currentStep === 'AccessCodeSubmission' && cobrand.id === CobrandIdentifier.WELLFRAME,
    submitButtonText,
    isValid,
    hasError,
    clearError,
  };
};
